/**
 * BestOddsRadar - Subscriber-facing card grid for the "Best Odds / Market
 * Anomalies" product. Mirrors SurebetList's state machine (loading/error/
 * empty/data) and stats header, but renders a responsive card grid (like
 * MaskedBestOddsPreview) instead of a stacked list, and opens
 * BestOddsDetailModal on card click instead of a calculator.
 *
 * The grid renders filteredAnomalies (client-side filters from BestOddsContext).
 */

import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useBestOdds } from '../context/BestOddsContext';
import { useTranslation } from '../../i18n/hooks';
import { logActivity, ACTIVITY_ACTIONS } from '../../analytics';
import BestOddsCard from './BestOddsCard';
import BestOddsDetailModal from './BestOddsDetailModal';
import BestOddsFilterBar from './BestOddsFilterBar';
import './BestOddsRadar.css';

const BestOddsRadar = ({ className = '', showStats = true, showRefresh = true }) => {
  const { t } = useTranslation();
  const {
    anomalies,
    filteredAnomalies,
    filteredCount,
    hasActiveFilters,
    resetFilters,
    loading,
    error,
    refreshAnomalies,
    isRefreshing,
    totalAnomalies,
    topSpread
  } = useBestOdds();

  const [selectedAnomaly, setSelectedAnomaly] = useState(null);

  const handleCardClick = useCallback((anomaly) => {
    logActivity(ACTIVITY_ACTIONS.ANOMALY_DETAIL_OPEN);
    setSelectedAnomaly(anomaly);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedAnomaly(null);
  }, []);

  const handleRefresh = async () => {
    await refreshAnomalies();
  };

  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // Close detail modal if the selected card is filtered out.
  useEffect(() => {
    if (!selectedAnomaly) {
      return;
    }
    const stillVisible = filteredAnomalies.some((item) => item.id === selectedAnomaly.id);
    if (!stillVisible) {
      setSelectedAnomaly(null);
    }
  }, [filteredAnomalies, selectedAnomaly]);

  if (loading && anomalies.length === 0) {
    return (
      <div className={`best-odds-radar best-odds-radar--loading ${className}`}>
        <div className="loading-spinner" />
        <p>{t('bestOdds.radar.loading', 'Зареждане на Best Odds сигнали...')}</p>
      </div>
    );
  }

  if (error && anomalies.length === 0) {
    return (
      <div className={`best-odds-radar best-odds-radar--error ${className}`}>
        <h3>{t('bestOdds.radar.errorTitle', 'Грешка при зареждане')}</h3>
        <p>{t('bestOdds.radar.errorDescription', 'Възникна грешка при зареждането на Best Odds сигналите.')}</p>
        <button type="button" className="best-odds-radar__retry-btn" onClick={handleRefresh}>
          {t('bestOdds.radar.retryButton', 'Опитай отново')}
        </button>
      </div>
    );
  }

  if (!loading && anomalies.length === 0) {
    return (
      <div className={`best-odds-radar best-odds-radar--empty ${className}`}>
        <h3>{t('bestOdds.radar.noDataTitle', 'Няма открити аномалии')}</h3>
        <p>{t('bestOdds.radar.noDataDescription', 'В момента няма открити пазарни аномалии в Tier 1 първенствата. Опитайте отново по-късно.')}</p>
        {showRefresh && (
          <button type="button" className="best-odds-radar__retry-btn" onClick={handleRefresh}>
            {t('bestOdds.radar.refreshButton', 'Опресняване')}
          </button>
        )}
      </div>
    );
  }

  const showFilteredEmpty = filteredCount === 0 && hasActiveFilters;

  return (
    <div className={`best-odds-radar ${className}`} role="region" aria-label={t('bestOdds.radar.ariaLabel', 'Списък с Best Odds сигнали')}>
      {(showStats || showRefresh) && (
        <div className="best-odds-radar__header">
          <div className="best-odds-radar__stats">
            <h1 className="best-odds-radar__title">{t('bestOdds.radar.title', 'Best Odds Радар')}</h1>
            {showStats && (
              <div className="best-odds-radar__stats-details">
                <span className="best-odds-radar__stat">
                  {t('bestOdds.radar.total', 'Активни аномалии')}: <strong>{totalAnomalies}</strong>
                </span>
                <span className="best-odds-radar__stat">
                  {t('bestOdds.radar.topSpread', 'Най-голямо отклонение')}: <strong>+{topSpread}%</strong>
                </span>
              </div>
            )}
          </div>

          {showRefresh && (
            <div className="best-odds-radar__actions">
              <button
                type="button"
                className="best-odds-radar__refresh-btn"
                onClick={handleRefresh}
                disabled={isRefreshing}
                aria-label={t('bestOdds.radar.refreshButton', 'Опресняване')}
              >
                <span className={isRefreshing ? 'best-odds-radar__refresh-icon--spinning' : ''}>⟳</span>
                <span>{t('bestOdds.radar.refreshButton', 'Опресняване')}</span>
              </button>
            </div>
          )}
        </div>
      )}

      <BestOddsFilterBar />

      {showFilteredEmpty ? (
        <div className="best-odds-radar__filtered-empty" role="status">
          <h3>{t('bestOdds.filters.emptyFilteredTitle')}</h3>
          <p>{t('bestOdds.filters.emptyFilteredDescription')}</p>
          <button
            type="button"
            className="best-odds-radar__retry-btn"
            onClick={handleClearFilters}
            aria-label={t('bestOdds.filters.clearAllAriaLabel')}
          >
            {t('bestOdds.filters.clearFilters')}
          </button>
        </div>
      ) : (
        <div className="best-odds-radar__grid">
          {filteredAnomalies.map((anomaly) => (
            <BestOddsCard key={anomaly.id} anomaly={anomaly} onClick={handleCardClick} />
          ))}
        </div>
      )}

      {isRefreshing && anomalies.length > 0 && (
        <div className="best-odds-radar__refresh-overlay" role="status" aria-live="polite">
          <span>{t('bestOdds.radar.refreshing', 'Обновяване...')}</span>
        </div>
      )}

      <BestOddsDetailModal
        anomaly={selectedAnomaly}
        isOpen={!!selectedAnomaly}
        onClose={handleCloseModal}
      />
    </div>
  );
};

BestOddsRadar.propTypes = {
  className: PropTypes.string,
  showStats: PropTypes.bool,
  showRefresh: PropTypes.bool
};

export default BestOddsRadar;
